<template>
  <b-row class="justify-content-md-center m-0">
    <div v-if="isFetching" class="d-flex justify-content-center p-4 m-3">
      <b-spinner label="Loading..." variant="dark" />
    </div>

    <b-col v-else xl="8" class="container__create-method mb-u5">
      <base-bread-crumb :items="breadcrumbs" class="bg-white" />
      <div class="d-flex justify-content-between">
        <h3 class="font-weight-bold mb-4">Create Method Components</h3>
        <b-button
          v-if="mode === 'view' && !isReviewer"
          variant="outline-primary h-100"
          class="button__preview-as-learner"
          @click="onPreviewAsLearner()"
        >
          Preview as learner
        </b-button>
      </div>

      <div class="column__create-method">
        <div class="column-left">
          <component
            :is="isLargeScreen ? 'div' : currentComponent"
            class="side-bar__create-method p-0"
          >
            <method-component-outline
              ref="methodComponentOutline"
              v-model="selectedIndex"
              class="method-component-outline mb-3"
              :method-components="methodComponents"
              :is-published="isReviewOrPublished"
              :mode="mode"
              :is-owner="isOwner"
              @onAddItem="onAddMethodComponent"
              @onSelectItem="onSelectItem"
            >
            </method-component-outline>

            <MethodReferenceOutline
              class="mb-3 reference-outline"
              :reference="reference"
              :mode="mode"
              :is-owner="isOwner"
              :has-reference="hasReference"
              :selected-index="selectedIndex"
              :is-published="isReviewOrPublished"
              @onSelectItem="onSelectItem"
              @update:reference="updateReference"
              @addReference="onAddReference"
            />

            <b-alert show class="alert__note">
              <p class="font-weight-bold mb-2">Note</p>
              <p>
                Designer can break down method component content into multiple topics for easier
                learner’s reading.
              </p>
            </b-alert>
          </component>
        </div>

        <div class="column-right">
          <div v-if="isSubmitting" class="d-flex justify-content-center p-4 m-3">
            <b-spinner label="Loading..." variant="dark" />
          </div>

          <div v-show="!isSubmitting">
            <MethodComponentEditor
              v-if="selectedMethodComponent"
              ref="methodComponentEditor"
              :mode="mode"
              :is-owner="isOwner"
              :method-component="selectedMethodComponent"
              :is-deletable="isDeletabel"
              :is-published="isReviewOrPublished"
              @submit:method-component="onSubmit"
              @create:method-component="onCreate"
              @edit:method-component="onEditing"
              @editing="onEditing"
              @canceled="onCanceled"
              @change-cancel="onChangeCancel"
              @delete="onDelete"
            />
            <MethodReference
              v-if="showReference"
              ref="methodReference"
              :mode="mode"
              :is-owner="isOwner"
              :reference="reference"
              :is-published="isReviewOrPublished"
              @editing="onEditing"
              @submit:method-reference="onSubmitReference"
              @canceled:method-reference="onCanceledReference"
              @change-cancel="onChangeCancel"
            />
          </div>
        </div>
      </div>
      <BaseBackToTopButton class="container-button__back-to-top" />
    </b-col>
  </b-row>
</template>

<script>
import MethodSidebar from './components/MethodSidebar.vue';
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb';
import LearningUnit from '@/models/LearningUnit';
import MethodComponents from '@/models/MethodComponent';
import MethodComponentOutline from './components/MethodComponentOutline.vue';
import MethodComponentEditor from './components/MethodComponentEditor.vue';
import MethodReferenceOutline from './components/MethodReferenceOutline.vue';
import MethodReference from './components/MethodReference.vue';
import BaseBackToTopButton from '@/components/base-components/BaseBackToTopButton.vue';

export default {
  components: {
    MethodSidebar,
    BaseBreadCrumb,
    MethodComponentOutline,
    MethodComponentEditor,
    MethodReferenceOutline,
    MethodReference,
    BaseBackToTopButton
  },

  data() {
    return {
      mode: 'view',
      methodComponents: [],
      selectedItemId: null,
      selectedIndex: 0,
      isLargeScreen: false,
      currentComponent: 'MethodSidebar',
      formReference: '',
      showReference: false,
      isFetching: false,
      isSubmitting: false,
      hasSubmissionError: false
    };
  },

  computed: {
    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    learningUnit() {
      return LearningUnit.query()
        .where('id', this.learningUnitId)
        .with('method_components')
        .first();
    },

    isReviewOrPublished() {
      return this.learningUnit?.required_review || this.learningUnit?.is_published;
    },

    breadcrumbs() {
      return [
        {
          key: 'design-planning',
          name: 'Design and planning',
          to: {
            name: 'design-planning'
          }
        },
        {
          key: 'learning-unit-info',
          name: this.learningUnit?.name ?? 'Micro-Credential',
          to: {
            name: 'design-planning/info',
            params: {
              learningUnitId: this.learningUnitId
            }
          }
        },
        {
          key: 'create-method',
          name: 'Create method components',
          active: true
        }
      ];
    },
    selectedMethodComponent() {
      return this.methodComponents[this.selectedIndex] ?? null;
    },
    isDeletabel() {
      return this.isOwner && this.methodComponents.length > 1 && this.selectedIndex !== 0;
    },
    hasReference() {
      return !!this.learningUnit?.reference;
    },
    reference() {
      return this.learningUnit?.reference;
    },
    isOwner() {
      return this.$currentUser?.isOwnerOf(this.learningUnitId);
    },
    isReviewer() {
      return this.$currentUser?.isReviewer();
    },
    isAssessorOfLearningUnit() {
      return this.$currentUser?.isAssessorOf(this.learningUnitId);
    }
  },
  watch: {
    $currentUser: {
      immediate: true,
      handler(newUser) {
        if (newUser) {
          this.checkUserPermission();
        }
      }
    }
  },
  created() {
    if (this.$currentUser) {
      this.checkUserPermission();
    }
  },

  mounted() {
    this.fetchLearningUnit();
    this.fetchMethodComponents();
    this.updateView();

    window.addEventListener('resize', this.updateView);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateView);
  },
  methods: {
    updateView() {
      this.isLargeScreen = window.innerWidth > 768;
    },

    checkUserPermission() {
      const hasPermission = this.isOwner || this.isReviewer || this.isAssessorOfLearningUnit;

      if (!hasPermission) {
        this.$router.push({
          path: '/'
        });
      }
    },

    async fetchLearningUnit() {
      if (!this.learningUnitId) return;
      try {
        this.isFetching = true;

        await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);
      } finally {
        this.isFetching = false;
      }
    },

    async fetchMethodComponents() {
      this.isFetching = true;
      const data = await MethodComponents.api().fetchMethodComponentsByLearningUnit(
        this.learningUnitId
      );

      if (this.isOwner && data.length === 0 && !this.learningUnit.is_published) {
        this.methodComponents = [
          {
            title: 'Introduction'
          }
        ];
        this.mode = 'create';
      } else {
        this.methodComponents = data;
        this.mode = 'view';
      }

      this.isFetching = false;
    },
    onSelectItem(indexes) {
      if (this.isSubmitting) return;
      if (this.mode === 'create') {
        if (this.$refs.methodComponentEditor) {
          this.$refs.methodComponentEditor.changeCancel(indexes.newVal);
        }
        if (this.$refs.methodReference) {
          this.$refs.methodReference.changeCancel(indexes.newVal);
        }

        return;
      } else if (this.mode === 'edit') {
        if (this.$refs.methodComponentEditor) {
          this.$refs.methodComponentEditor.changeCancel(indexes.newVal);
        }
        if (this.$refs.methodReference) {
          this.$refs.methodReference.changeCancel(indexes.newVal);
        }
        return;
      }

      this.$set(this, 'selectedIndex', indexes.newVal);

      if (this.selectedIndex === null) {
        this.showReference = true;
      } else {
        this.showReference = false;
      }
      // Close MethodReference and show MethodComponentEditor
    },
    onAddMethodComponent() {
      this.methodComponents.push({
        title: 'New Method Component'
      });

      this.$set(this, 'selectedIndex', this.methodComponents.length - 1);
      this.mode = 'create';
      if (this.showReference) {
        this.showReference = false;
      }
    },
    async onCreate(data = {}) {
      data = {
        ...data,
        learning_unit: this.learningUnitId
      };

      try {
        this.isSubmitting = true;
        const method_component = await MethodComponents.api().createMethodComponent(data);

        if (this.$refs.methodComponentEditor) {
          let attachments = await this.$refs.methodComponentEditor.uploadFiles(method_component.id);
          this.$set(method_component, 'attachments', attachments);
        }

        this.$set(this.methodComponents, this.selectedIndex, method_component);

        this.showSuccessToast('method', 'created');
      } catch (error) {
        this.showErrorToast('method', 'create', error.response.data.message);
      } finally {
        this.isSubmitting = false;
      }
    },

    async onSubmit(data) {
      if (this.mode === 'create') {
        this.onCreate(data);
      } else if (this.mode === 'edit') {
        this.onUpdate(data);
      }

      this.mode = 'view';
    },
    async onDelete() {
      this.isSubmitting = true;
      const id = this.methodComponents[this.selectedIndex].id;

      await MethodComponents.api().deleteMethodComponent(id);
      this.methodComponents = this.methodComponents.filter(
        (methodComponent) => methodComponent.id !== id
      );
      this.deleteMethod();
      this.mode = 'view';
      this.isSubmitting = false;
    },

    async onUpdate(data = {}) {
      try {
        this.isSubmitting = true;
        const method_component = await MethodComponents.api().updateMethodComponent(
          this.methodComponents[this.selectedIndex].id,
          data
        );

        if (this.$refs.methodComponentEditor) {
          let attachments = await this.$refs.methodComponentEditor.uploadFiles(method_component.id);
          this.$set(method_component, 'attachments', attachments);
        }

        this.$set(this.methodComponents, this.selectedIndex, method_component);

        this.mode = 'view';
        this.isSubmitting = false;
        this.showSuccessToast('method', 'updated');
      } catch (error) {
        this.showErrorToast('method', 'update', error.response.data.message);
      } finally {
        this.isSubmitting = false;
      }
    },
    onEditing() {
      this.mode = 'edit';
    },
    onChangeCancel(index) {
      if (this.hasSubmissionError) {
        window.location.reload();
      }

      if (this.mode === 'create') {
        this.methodComponents.splice(this.selectedIndex, 1);

        if (index !== null) {
          this.$set(this, 'selectedIndex', index);
        } else {
          this.deleteMethod();
        }
      } else {
        this.$set(this, 'selectedIndex', index);
      }

      this.mode = 'view';
      if (this.selectedIndex === null) {
        this.showReference = true;
      } else {
        this.showReference = false;
      }
    },
    onCanceled() {
      if (this.hasSubmissionError) {
        window.location.reload();
      }

      if (this.mode === 'create') {
        this.methodComponents.splice(this.selectedIndex, 1);
        this.deleteMethod();
      }
      this.mode = 'view';
    },

    deleteMethod() {
      this.$refs.methodComponentOutline.deleteMethod();
    },

    async updateReference() {
      this.isSubmitting = true;
      const body = {
        reference: this.formreference
      };
      await LearningUnit.api().updateLearningUnit(this.learningUnitId, body);
      this.mode = 'view';
      this.isSubmitting = false;
    },

    onAddReference() {
      this.showReference = true;
      this.mode = 'create';
      this.selectedIndex = null; // Deselect item in MethodComponentOutline
    },

    async onSubmitReference(data) {
      const toastAlertMode = this.mode === 'create' ? 'created' : 'updated';
      const failedAlertMode = this.mode === 'create' ? 'create' : 'update';
      try {
        this.isSubmitting = true;

        const body = { reference: data };
        const { response } = await LearningUnit.api().checkMcIsDraft(this.learningUnitId);
        const isDraft = response?.data?.isDraft;

        if (isDraft) {
          await LearningUnit.api().updateLearningUnit(this.learningUnitId, body);
          this.mode = 'view';
          this.showSuccessToast('reference', toastAlertMode);
        } else {
          this.showErrorToast('reference', failedAlertMode);
        }
      } catch (error) {
        this.$toast.open({
          message: error.response.data.message,
          type: 'error',
          pauseOnHover: true,
          duration: 5000
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    onCanceledReference(index) {
      if (this.hasSubmissionError) {
        window.location.reload();
      }

      if (this.mode === 'create') {
        this.formreference = '';
        if (index !== null) {
          this.$set(this, 'selectedIndex', index);
        } else {
          this.deleteMethod();
        }
      }
      if (index !== null) {
        this.$set(this, 'selectedIndex', index);
      } else {
        this.deleteMethod();
      }
      this.mode = 'view';

      this.showReference = false;
    },

    getPreviewRoute() {
      return this.$router.resolve({
        name: 'learning-unit/method-components-preview',
        params: {
          learningUnitId: this.learningUnitId
        }
      });
    },

    showSuccessToast(title, action) {
      const previewURL = this.getPreviewRoute().href;
      const isMethod = title === 'method';
      const entity = isMethod ? 'Method components topic' : 'Reference';

      this.$toast.open({
        message: `${entity} ${action} successfully.
        <div>Click <a class="link__preview-as-learner" href="${previewURL}" onclick="window.open(this.href, '_blank'); return false;">Preview as learner</a> to see the changes.</div>`,
        type: 'success',
        pauseOnHover: true,
        duration: 5000
      });
    },

    showErrorToast(title, action, errorMessage) {
      const isMethod = title === 'method';
      const entity = isMethod ? 'Method components topic' : 'Reference';
      errorMessage = errorMessage || `Failed to ${action} ${entity} . Please try again.`;

      this.hasSubmissionError = true;
      this.mode = action === 'create' ? 'create' : 'edit';

      this.$toast.open({
        message: errorMessage,
        type: 'error',
        pauseOnHover: true,
        duration: 5000
      });
    },

    onPreviewAsLearner() {
      window.open(this.getPreviewRoute().href, '_blank');
    }
  }
};
</script>

<style lang="scss">
.container__create-method {
  max-width: 1140px;
  margin: 0 auto;

  .column__create-method {
    max-width: 1140px;
    display: grid;
    grid-template-columns: 340px 1fr;
    gap: 40px;
  }

  .side-bar__create-method {
    display: flex;
    flex-direction: column;
  }

  .alert__note {
    color: #3b3e66;
    background-color: #e8fbfd;
    border-color: #11c5db;
  }

  @media screen and (max-width: 768px) {
    .column__create-method {
      grid-template-columns: 1fr;
    }
  }
}
</style>
